import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import {
  FavouriteButton,
  IconShipping,
  Image as ImageComponent,
  Price as PriceComponent,
} from '@noths/polaris-client-ribbons-design-system';
import { getProductImageURL } from '@noths/polaris-client-utils';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { PrimaryLabelType } from '../../types';
import * as testIds from './constants/testIds';
import * as styles from './ProductCard.styles';

export interface ProductCardProps {
  code: number;
  currency: CurrencyCode;
  deliveryEventMessage?: string;
  deliveryLabelText?: string;
  favourite?: boolean;
  href: string;
  imageLoading?: 'eager' | 'lazy';
  imageSrc: string;
  onFavouriteClick?: ({ productCode }: { productCode: number }) => void;
  onProductClick: (
    evt: React.MouseEvent<HTMLAnchorElement>,
    { productCode }: { productCode: number },
  ) => void;
  onProductMouseDown?: (
    evt: React.MouseEvent<HTMLAnchorElement>,
    { productCode }: { productCode: number },
  ) => void;
  onVisible?: ({ productCode }: { productCode: number }) => void;
  partnerName: string;
  preSalePrice?: number;
  price: number;
  primaryLabelText?: string;
  primaryLabelType?: PrimaryLabelType;
  title: string;
}

export const ProductCard = ({
  code,
  currency,
  deliveryEventMessage,
  deliveryLabelText,
  favourite = false,
  href,
  imageLoading = 'lazy',
  imageSrc,
  onFavouriteClick,
  onProductClick,
  onProductMouseDown = () => {},
  onVisible = () => {},
  partnerName,
  preSalePrice,
  price,
  primaryLabelText,
  primaryLabelType = 'new',
  title,
}: ProductCardProps) => {
  const rootElRef = useImpressionRef<HTMLDivElement>(() => onVisible({ productCode: code }));

  return (
    <article css={styles.outerCard} data-testid="product-card" ref={rootElRef}>
      <a
        css={styles.link}
        href={href}
        onClick={(evt) => onProductClick(evt, { productCode: code })}
        onMouseDown={(evt) => onProductMouseDown(evt, { productCode: code })}
      >
        <div css={styles.topInnerCard}>
          <ImageComponent loading={imageLoading} src={getProductImageURL(imageSrc)} />
          {primaryLabelText && (
            <div css={styles.imageLabels(!primaryLabelText)}>
              {primaryLabelText && (
                <b
                  css={styles.primaryLabel(primaryLabelType)}
                  data-testid={testIds.PRIMARY_LABEL_TEST_ID}
                >
                  {primaryLabelText}
                </b>
              )}
            </div>
          )}
          <p aria-hidden css={styles.soldBy}>
            {partnerName}
          </p>
          <h3 css={styles.title}>{title}</h3>
        </div>
      </a>
      <div css={styles.bottomInnerCard}>
        <PriceComponent
          amount={price}
          css={styles.currentPrice}
          currency={currency}
          testId={testIds.PRICE}
          type={preSalePrice !== undefined ? 'sale' : 'normal'}
        />
        {preSalePrice && (
          <PriceComponent
            amount={preSalePrice}
            css={styles.previousPrice}
            currency={currency}
            testId={testIds.PREVIOUS_PRICE}
            type="previous"
          />
        )}
        {deliveryEventMessage && (
          <p css={styles.deliveryEvent}>
            <strong>{deliveryEventMessage}</strong>
          </p>
        )}
      </div>

      {deliveryLabelText && (
        <span css={styles.deliveryLabel}>
          <IconShipping color={`var(--brand-purple-base)`} height={16} width={16} />
          <span dangerouslySetInnerHTML={{ __html: deliveryLabelText }} />
        </span>
      )}
      <FavouriteButton
        active={favourite}
        css={styles.favouriteButtonWrapper}
        onClick={() => onFavouriteClick?.({ productCode: code })}
        size="large"
      />
    </article>
  );
};
